
import AppsIcon from "vue-material-design-icons/Apps.vue";
import CheckCircleIcon from "vue-material-design-icons/CheckCircleOutline.vue";
import ClockIcon from "vue-material-design-icons/ClockOutline.vue";
import CreditCardIcon from "vue-material-design-icons/CreditCardOutline.vue";
import MagnifyIcon from "vue-material-design-icons/Magnify.vue";
import OpenInNewIcon from "vue-material-design-icons/OpenInNew.vue";
import PuzzleIcon from "vue-material-design-icons/PuzzleOutline.vue";
import { mapGetters } from "vuex";

const STATUS = {
  INSTALLED: "installed",
  AVAILABLE: "available",
  PENDING_PAYMENT: "pending_payment",
  UNAVAILABLE: "unavailable",
  COMING_SOON: "coming_soon",
};
const USERS_EXTRA_CODE = "PLG_USUARIOS_EXTRA";
const MARKETPLACE_MODULE_SEGMENTS = {
  MOD_CRM: "CRM",
  MOD_FOOD: "Food",
  MOD_SERVICOS: "Serviços",
  MOD_TRANSPORTE: "Transporte",
};
const RECOMMENDED_FILTER_PRIORITY = [
  "crm",
  "food",
  "servicos",
  "transporte",
];

export default {
  name: "appsIndex",
  components: {
    AppsIcon,
    CheckCircleIcon,
    ClockIcon,
    CreditCardIcon,
    MagnifyIcon,
    OpenInNewIcon,
    PuzzleIcon,
  },
  data() {
    return {
      apps: [],
      isLoading: false,
      isConfirmingCheckout: false,
      loadingCheckoutCode: null,
      showCheckoutReturnMessage: false,
      checkoutReturnMessage: "",
      checkoutReturnVariant: "info",
      search: "",
      selectedFilter: "all",
      selectedApp: null,
      selectedAppQuantity: 1,
      selectedCheckoutMode: "contract",
      appQuantities: {},
    };
  },
  computed: {
    ...mapGetters(["isClienteNutsDireto"]),
    availableFilters() {
      return [
        { label: "Todos", value: "all" },
        { label: "Módulos", value: "module" },
        { label: "Plugins", value: "plugin" },
        ...this.recommendedCategoryFilters,
        { label: "Instalados", value: STATUS.INSTALLED },
        { label: "Disponíveis", value: STATUS.AVAILABLE },
        { label: "Em breve", value: "coming_soon_group" },
      ];
    },
    recommendedCategoryFilters() {
      const labelsByKey = {};

      this.apps.forEach((app) => {
        this.normalizedRecommendedFor(app).forEach((category) => {
          const key = this.segmentKey(category);
          if (key && !labelsByKey[key]) {
            labelsByKey[key] = category;
          }
        });
      });

      const categories = Object.keys(labelsByKey).map((key) => ({
        label: labelsByKey[key],
        value: `segment:${key}`,
      }));

      return categories.sort((first, second) => {
        const firstKey = first.value.replace("segment:", "");
        const secondKey = second.value.replace("segment:", "");
        const firstPriority = RECOMMENDED_FILTER_PRIORITY.indexOf(firstKey);
        const secondPriority = RECOMMENDED_FILTER_PRIORITY.indexOf(secondKey);

        if (firstPriority !== -1 || secondPriority !== -1) {
          if (firstPriority === -1) return 1;
          if (secondPriority === -1) return -1;
          if (firstPriority !== secondPriority) return firstPriority - secondPriority;
        }

        return first.label.localeCompare(second.label, "pt-BR", {
          sensitivity: "base",
        });
      });
    },
    canAccessMarketplace() {
      return this.isClienteNutsDireto;
    },
    filteredApps() {
      const term = this.normalizeText(this.search);

      return this.apps.filter((app) => {
        const type = this.normalizedType(app);
        const status = app.status;
        const selectedSegment = this.selectedRecommendedSegment();
        const matchesFilter =
          this.selectedFilter === "all" ||
          this.selectedFilter === type ||
          this.selectedFilter === status ||
          (this.selectedFilter === "coming_soon_group" &&
            this.isComingSoonStatus(status)) ||
          (selectedSegment && this.matchesRecommendedSegment(app, selectedSegment));
        const matchesSearch = !term || this.searchableText(app).includes(term);

        return matchesFilter && matchesSearch;
      });
    },
  },
  created() {
    if (!this.ensureMarketplaceAccess()) return;

    this.handleCheckoutReturn();
    this.fetchApps();
  },
  watch: {
    "$route.query.checkout"() {
      if (!this.ensureMarketplaceAccess()) return;

      if (this.handleCheckoutReturn()) {
        this.fetchApps();
      }
    },
    availableFilters(filters) {
      if (filters.some((filter) => filter.value === this.selectedFilter)) return;
      this.selectedFilter = "all";
    },
  },
  methods: {
    ensureMarketplaceAccess() {
      if (this.canAccessMarketplace) return true;

      this.$toast.warning("Esta funcionalidade não está disponível para sua conta.");
      this.$router.replace({ name: "dashboard" });
      return false;
    },
    async fetchApps() {
      if (!this.canAccessMarketplace) return;

      this.isLoading = true;

      try {
        const { data } = await this.$http.get("/apps");
        const apps = this.normalizeAppsResponse(data);
        this.apps = apps;
        this.initializeAppQuantities(apps);
      } catch (error) {
        this.$toast.warning("Não foi possível carregar os apps.");
      } finally {
        this.isLoading = false;
      }
    },
    normalizeAppsResponse(data) {
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.data)) return data.data;
      if (Array.isArray(data?.apps)) return data.apps;
      return [];
    },
    handleCheckoutReturn() {
      const checkoutStatus = this.$route?.query?.checkout;
      const messages = {
        success:
          "Pagamento recebido ou em processamento. Em instantes o app será liberado após confirmação do Asaas.",
        cancel: "Checkout cancelado. Nenhum app foi contratado.",
        expired: "O checkout expirou. Gere um novo pagamento para continuar.",
      };
      const variants = {
        success: "info",
        cancel: "warning",
        expired: "warning",
      };

      this.showCheckoutReturnMessage = !!messages[checkoutStatus];
      this.checkoutReturnMessage = messages[checkoutStatus] || "";
      this.checkoutReturnVariant = variants[checkoutStatus] || "info";

      return this.showCheckoutReturnMessage;
    },
    normalizeText(value) {
      return String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },
    normalizeStringArray(value) {
      if (Array.isArray(value)) {
        return value.map((item) => String(item || "").trim()).filter(Boolean);
      }

      if (typeof value === "string") {
        return value
          .split(/\n|,|;|\|/)
          .map((item) => item.trim())
          .filter(Boolean);
      }

      return [];
    },
    appDescription(app) {
      return (
        app.short_description ||
        app.description ||
        app.descricao_curta ||
        app.descricao ||
        ""
      );
    },
    normalizedRecommendedFor(app) {
      return this.normalizeStringArray(app.recommended_for || app.recommendedFor);
    },
    normalizedTags(app) {
      return this.normalizeStringArray(app.tags);
    },
    segmentKey(value) {
      return this.normalizeText(value || "").replace(/\s+/g, "-");
    },
    selectedRecommendedSegment() {
      if (!this.selectedFilter.startsWith("segment:")) return "";
      return this.selectedFilter.replace("segment:", "");
    },
    moduleSegmentKeys(app) {
      if (this.normalizedType(app) !== "module") return [];

      const keys = new Set();
      const code = this.normalizedAppCode(app);
      const mappedSegment = MARKETPLACE_MODULE_SEGMENTS[code];

      if (mappedSegment) {
        keys.add(this.segmentKey(mappedSegment));
      }

      keys.add(this.segmentKey(app.name));

      return Array.from(keys).filter(Boolean);
    },
    matchesRecommendedSegment(app, segment) {
      const recommendedKeys = this.normalizedRecommendedFor(app).map((category) =>
        this.segmentKey(category)
      );

      if (recommendedKeys.includes(segment)) return true;

      return this.moduleSegmentKeys(app).includes(segment);
    },
    searchableText(app) {
      const benefits = this.normalizedBenefits(app).join(" ");
      const recommendedFor = this.normalizedRecommendedFor(app).join(" ");
      const tags = this.normalizedTags(app).join(" ");

      return this.normalizeText(
        [app.name, this.appDescription(app), benefits, recommendedFor, tags]
          .filter(Boolean)
          .join(" ")
      );
    },
    normalizedType(app) {
      const type = this.normalizeText(app.type || app.tipo);
      if (type === "plugin") return "plugin";
      return "module";
    },
    appTypeLabel(app) {
      return this.normalizedType(app) === "plugin" ? "Plugin" : "Módulo";
    },
    appTypeClass(app) {
      return this.normalizedType(app) === "plugin"
        ? "app-icon-plugin"
        : "app-icon-module";
    },
    appKey(app) {
      return this.appCode(app) || app.name;
    },
    appCode(app) {
      return app.code || app.codigo || app.id;
    },
    normalizedAppCode(app) {
      return String(this.appCode(app) || "").toUpperCase();
    },
    isUsersExtraApp(app) {
      return this.normalizedAppCode(app) === USERS_EXTRA_CODE;
    },
    billingMode(app) {
      return this.normalizeText(app.billing_mode || app.modo_cobranca) ===
        "per_quantity"
        ? "per_quantity"
        : "fixed";
    },
    isPerQuantity(app) {
      return this.billingMode(app) === "per_quantity";
    },
    parseInteger(value, fallback = 0) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Math.floor(parsed) : fallback;
    },
    minQuantity(app) {
      if (!this.isPerQuantity(app)) return 1;

      const min = this.parseInteger(app.min_quantity, 1);
      return min > 0 ? min : 1;
    },
    maxQuantity(app) {
      if (!this.isPerQuantity(app)) return 1;

      const min = this.minQuantity(app);
      const max = this.parseInteger(app.max_quantity, min);
      const normalizedMax = max >= min ? max : min;

      if (this.canAddQuantity(app)) {
        return Math.max(min, normalizedMax - this.installedQuantity(app));
      }

      return normalizedMax;
    },
    quantityLabel(app) {
      if (this.isUsersExtraApp(app)) {
        return "usuário extra";
      }

      const label = String(app.quantity_label || "item").trim();
      return label || "item";
    },
    unitPriceLabel(app) {
      if (this.isUsersExtraApp(app)) {
        return "usuário adicional";
      }

      return String(app.unit_price_label || this.quantityLabel(app)).trim();
    },
    quantityLabelPlural(app, quantity) {
      if (this.isUsersExtraApp(app)) {
        return quantity === 1 ? "usuário extra" : "usuários extras";
      }

      const label = this.quantityLabel(app);

      if (quantity === 1 || label.toLowerCase().endsWith("s")) {
        return label;
      }

      return `${label}s`;
    },
    addQuantityLabelPlural(app, quantity) {
      if (this.isUsersExtraApp(app)) {
        return quantity === 1 ? "usuário" : "usuários";
      }

      return this.quantityLabelPlural(app, quantity);
    },
    installedQuantity(app) {
      return Math.max(0, this.parseInteger(app.installed_quantity, 0));
    },
    usedQuantity(app) {
      return Math.max(0, this.parseInteger(app.used_quantity, 0));
    },
    availableInstalledQuantity(app) {
      return Math.max(0, this.installedQuantity(app) - this.usedQuantity(app));
    },
    defaultQuantity(app) {
      return this.isPerQuantity(app) ? this.minQuantity(app) : 1;
    },
    normalizeSelectedQuantity(app, quantity) {
      if (!this.isPerQuantity(app)) return 1;

      const numericQuantity = this.parseInteger(quantity, this.defaultQuantity(app));
      return Math.min(this.maxQuantity(app), Math.max(this.minQuantity(app), numericQuantity));
    },
    selectedQuantity(app) {
      const code = this.appCode(app);

      if (!code) return this.defaultQuantity(app);

      return this.normalizeSelectedQuantity(app, this.appQuantities[code]);
    },
    setSelectedQuantity(app, quantity) {
      const code = this.appCode(app);
      if (!code) return;

      this.$set(
        this.appQuantities,
        code,
        this.normalizeSelectedQuantity(app, quantity)
      );
    },
    initializeAppQuantities(apps) {
      const quantities = {};

      apps.forEach((app) => {
        const code = this.appCode(app);
        if (!code) return;

        quantities[code] = this.defaultQuantity(app);
      });

      this.appQuantities = quantities;
    },
    increaseQuantity(app) {
      this.setSelectedQuantity(app, this.selectedQuantity(app) + 1);
    },
    decreaseQuantity(app) {
      this.setSelectedQuantity(app, this.selectedQuantity(app) - 1);
    },
    showQuantitySelector(app) {
      return (
        this.canCheckoutApp(app) &&
        this.isPerQuantity(app) &&
        (app.status === STATUS.AVAILABLE || this.canAddQuantity(app))
      );
    },
    showInstalledUsage(app) {
      return app.status === STATUS.INSTALLED && this.installedQuantity(app) > 0;
    },
    normalizedBenefits(app) {
      const benefits = app.benefits || app.beneficios || app.benefits_main;

      if (Array.isArray(benefits)) {
        return benefits.filter(Boolean);
      }

      if (typeof benefits === "string") {
        return benefits
          .split(/\n|;/)
          .map((benefit) => benefit.trim())
          .filter(Boolean);
      }

      return [];
    },
    monthlyPrice(app) {
      return Number(
        app.monthly_price ||
          app.valor_mensal ||
          app.price_monthly ||
          app.price ||
          app.valor ||
          0
      );
    },
    totalMonthlyPrice(app) {
      return this.monthlyPrice(app) * this.selectedQuantity(app);
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(Number(value) || 0);
    },
    monthlyPriceLabel(app) {
      return this.formatCurrency(this.monthlyPrice(app));
    },
    totalMonthlyPriceLabel(app) {
      return this.formatCurrency(this.totalMonthlyPrice(app));
    },
    quantitySelectorLabel(app) {
      if (this.canAddQuantity(app)) {
        return "Quantidade adicional";
      }

      return `Quantidade de ${this.quantityLabelPlural(app, 2)}`;
    },
    quantityTotalLabel(app) {
      if (this.canAddQuantity(app)) {
        return "Total adicional";
      }

      return "Total mensal";
    },
    statusLabel(status) {
      const labels = {
        [STATUS.INSTALLED]: "Instalado",
        [STATUS.AVAILABLE]: "Disponível",
        [STATUS.PENDING_PAYMENT]: "Pendente",
        [STATUS.UNAVAILABLE]: "Em breve",
        [STATUS.COMING_SOON]: "Em breve",
      };

      return labels[status] || "Disponível";
    },
    statusTooltip(status) {
      const labels = {
        [STATUS.PENDING_PAYMENT]: "Aguardando confirmação de pagamento.",
      };

      return labels[status] || this.statusLabel(status);
    },
    statusClass(status) {
      const classes = {
        [STATUS.INSTALLED]: "status-installed",
        [STATUS.AVAILABLE]: "status-available",
        [STATUS.PENDING_PAYMENT]: "status-pending",
        [STATUS.UNAVAILABLE]: "status-unavailable",
        [STATUS.COMING_SOON]: "status-coming-soon",
      };

      return classes[status] || classes[STATUS.AVAILABLE];
    },
    appCardClass(app) {
      const status = app?.status;

      if (status === STATUS.AVAILABLE) return "app-card-available";
      if (status === STATUS.INSTALLED) return "app-card-installed";
      if (status === STATUS.PENDING_PAYMENT) return "app-card-pending";
      return "app-card--coming-soon";
    },
    isComingSoonStatus(status) {
      return [STATUS.UNAVAILABLE, STATUS.COMING_SOON].includes(status);
    },
    actionLabel(app) {
      if (app.status === STATUS.INSTALLED) {
        if (this.canAddQuantity(app)) {
          return `Contratar mais ${this.addQuantityLabelPlural(app, 2)}`;
        }

        return "Instalado";
      }
      if (app.status === STATUS.PENDING_PAYMENT) {
        return app.checkout_url ? "Continuar pagamento" : "Gerar pagamento";
      }
      if (this.isComingSoonStatus(app.status)) return "Em breve";

      if (this.isPerQuantity(app)) {
        const quantity = this.selectedQuantity(app);
        const label = this.quantityLabelPlural(app, quantity);
        return `Contratar ${quantity} ${label} por ${this.totalMonthlyPriceLabel(
          app
        )}/mês`;
      }

      return `Contratar por ${this.monthlyPriceLabel(app)}/mês`;
    },
    actionIcon(app) {
      if (app.status === STATUS.INSTALLED) {
        return this.canAddQuantity(app) ? "CreditCardIcon" : "CheckCircleIcon";
      }
      if (app.status === STATUS.PENDING_PAYMENT) return "OpenInNewIcon";
      if (this.isComingSoonStatus(app.status)) return "ClockIcon";

      return "CreditCardIcon";
    },
    actionButtonClass(app) {
      if (app.status === STATUS.AVAILABLE) return "btn-success";
      if (app.status === STATUS.PENDING_PAYMENT) return "btn-warning";
      if (app.status === STATUS.INSTALLED) {
        return this.canAddQuantity(app) ? "btn-success" : "btn-light-success";
      }

      return "btn-light-secondary btn-coming-soon";
    },
    isActionDisabled(app) {
      if (app.status === STATUS.INSTALLED) {
        return !this.canAddQuantity(app);
      }

      return [STATUS.UNAVAILABLE, STATUS.COMING_SOON].includes(app.status);
    },
    canAddQuantity(app) {
      return (
        !!app &&
        this.canAccessMarketplace &&
        app.status === STATUS.INSTALLED &&
        this.isPerQuantity(app) &&
        app.can_add_quantity !== false
      );
    },
    canCheckoutApp(app) {
      return (
        this.canAccessMarketplace &&
        ([STATUS.AVAILABLE, STATUS.PENDING_PAYMENT].includes(app.status) ||
          this.canAddQuantity(app))
      );
    },
    showActionButton(app) {
      if (
        [STATUS.INSTALLED, STATUS.UNAVAILABLE, STATUS.COMING_SOON].includes(
          app.status
        )
      ) {
        return true;
      }

      return this.canCheckoutApp(app);
    },
    handleAction(app) {
      if (!this.canCheckoutApp(app)) return;

      if (app.status === STATUS.PENDING_PAYMENT) {
        this.continuePayment(app);
        return;
      }

      if (app.status === STATUS.AVAILABLE) {
        this.selectedApp = app;
        this.selectedAppQuantity = this.selectedQuantity(app);
        this.selectedCheckoutMode = "contract";
        this.$refs.contractAppModal.show();
        return;
      }

      if (this.canAddQuantity(app)) {
        this.selectedApp = app;
        this.selectedAppQuantity = this.selectedQuantity(app);
        this.selectedCheckoutMode = "add_quantity";
        this.$refs.contractAppModal.show();
      }
    },
    async continuePayment(app) {
      if (!this.canCheckoutApp(app)) return;

      if (app.checkout_url) {
        window.location.href = app.checkout_url;
        return;
      }

      await this.startCheckout(app, this.selectedQuantity(app));
    },
    hideContractModal() {
      this.$refs.contractAppModal.hide();
      this.selectedApp = null;
      this.selectedAppQuantity = 1;
      this.selectedCheckoutMode = "contract";
    },
    contractModalTitle() {
      if (!this.selectedApp) return "Contratar app";

      if (this.selectedCheckoutMode === "add_quantity") {
        return "Contratar quantidade adicional";
      }

      return "Contratar app";
    },
    contractModalText() {
      if (!this.selectedApp) return "";

      if (this.selectedCheckoutMode === "add_quantity") {
        const quantity = this.selectedAppQuantity;
        const label = this.addQuantityLabelPlural(this.selectedApp, quantity);

        return `Você deseja adicionar ${quantity} ${label} ao app ${
          this.selectedApp.name
        }?`;
      }

      if (!this.isPerQuantity(this.selectedApp)) {
        return `Você deseja contratar o app ${this.selectedApp.name} por ${this.monthlyPriceLabel(this.selectedApp)}/mês?`;
      }

      const quantity = this.selectedAppQuantity;
      const label = this.quantityLabelPlural(this.selectedApp, quantity);
      const total = this.totalMonthlyPriceLabel(this.selectedApp);

      return `Você deseja contratar o app ${this.selectedApp.name} com ${quantity} ${label} por ${total}/mês?`;
    },
    contractModalTotalLabel() {
      if (!this.selectedApp) return this.formatCurrency(0);
      return this.formatCurrency(
        this.monthlyPrice(this.selectedApp) * this.selectedAppQuantity
      );
    },
    async confirmCheckout() {
      if (!this.selectedApp) return;
      if (!this.canCheckoutApp(this.selectedApp)) return;

      this.isConfirmingCheckout = true;
      await this.startCheckout(
        this.selectedApp,
        this.selectedAppQuantity,
        this.selectedCheckoutMode
      );
      this.isConfirmingCheckout = false;
    },
    checkoutQuantity(app, quantity) {
      if (!this.isPerQuantity(app)) return 1;
      return this.normalizeSelectedQuantity(app, quantity);
    },
    async startCheckout(app, quantity = 1, checkoutMode = "contract") {
      if (!this.canCheckoutApp(app)) {
        this.$toast.warning(
          "Esta funcionalidade não está disponível para sua conta."
        );
        return;
      }

      const code = this.appCode(app);

      if (!code) {
        this.$toast.warning("App sem código para contratação.");
        return;
      }

      this.loadingCheckoutCode = code;

      try {
        const payload = {
          quantity: this.checkoutQuantity(app, quantity),
        };

        if (checkoutMode === "add_quantity") {
          payload.checkout_mode = "add_quantity";
        }

        const { data } = await this.$http.post(
          `/apps/${code}/checkout`,
          payload
        );

        if (data?.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }

        this.$toast.warning("Checkout indisponível para este app.");
      } catch (error) {
        this.$toast.warning("Não foi possível iniciar o pagamento.");
      } finally {
        this.loadingCheckoutCode = null;
      }
    },
  },
};
